import { Logger } from "./logger";
import { injectFormIntoDefaultPage } from "./injectDefaultForm";
import { injectFooterIntoDefaultPage } from "./injecDefaultFooter";
import { setLandingPageId } from "../context/landingPageContext";
import { replacePlaceholdersInDOM } from "./replacePlaceholdersInDOM";
import { setRecommendationData } from "../context/recommendationContext";
import { lazyLoadObserver } from "./intersectionObserver";
import { Spinner } from "../helpers/spinner";
import { LandingPageMetaDataModel } from "../models/landingPageMetaDataModel";
import { renderPreviewBanner } from "../utils/renderPreviewBanner";

export async function loadStaticDefaultLandingPage(metaData: LandingPageMetaDataModel, promoterName?: string): Promise<void> {
  const spinner = Spinner.getInstance("loading-spinner");
  spinner.show();

  try {
    Logger.info("🌐 Lade statische Default LandingPage...");

    const response = await fetch("/default.html");
    if (!response.ok) {
      throw new Error(`HTTP-Fehler beim Laden von default.html: ${response.status}`);
    }

    const html = await response.text();
    document.documentElement.innerHTML = html;
    renderPreviewBanner();

    // Nach dem DOM-Replacement: Spinner neu initialisieren
    Spinner.getInstance("loading-spinner");

    Logger.info("✅ Statische HTML-Seite geladen.");

    setLandingPageId(metaData.landingPageId);

    injectCompanyData(metaData);

    if (promoterName) {
      setRecommendationData({ promoterName });
    }

    replacePlaceholdersInDOM();

    await injectFormIntoDefaultPage();
    await injectFooterIntoDefaultPage(metaData.companyData?.name);

    lazyLoadObserver.observeDefaults();

  } catch (err) {
    Logger.error("❌ Fehler beim Laden der statischen Default LandingPage", err);
    showErrorFallback();
  } finally {
    spinner.hide();
  }
}

function injectCompanyData(metaData: LandingPageMetaDataModel): void {
  Logger.info("🏷️ Setze CompanyData in statische Seite:", metaData);

  const mappings: Record<string, string | undefined> = {
    ".company-name": metaData.companyData?.name,
    ".company-address": metaData.companyData?.address,
    ".company-postcode": metaData.companyData?.postCode,
    ".company-place": metaData.companyData?.place,
    ".company-phone": metaData.companyData?.phoneNumber,
    ".company-website": metaData.companyData?.websiteURL,
    ".company-email": metaData.contactEmailAddress,
  };

  Object.entries(mappings).forEach(([selector, value]) => {
    if (value) {
      const element = document.querySelector(selector);
      if (element) {
        element.textContent = value;
        Logger.info(`✅ ${selector} →`, value);
      } else {
        Logger.warn(`⚠️ Kein Element für Selector ${selector} gefunden.`);
      }
    }
  });

  // Unternehmenslogo
  if (metaData.thumbnailDownloadURL?.trim()) {
    const logoElement = document.querySelector<HTMLImageElement>(".company-logo");
    if (logoElement) {
      logoElement.src = metaData.thumbnailDownloadURL;
      logoElement.alt = metaData.companyData?.name || "Unternehmenslogo";
      Logger.info(`✅ Unternehmenslogo gesetzt: ${metaData.thumbnailDownloadURL}`);
    } else {
      Logger.warn("⚠️ Kein .company-logo Element für Unternehmenslogo gefunden.");
    }
  }

  // Hero-Bild
  if (metaData.landingPageImageDownloadURL?.trim()) {
    const heroElement = document.querySelector<HTMLElement>(".landingpage-hero");
    if (heroElement) {
      heroElement.style.backgroundImage = `url('${metaData.landingPageImageDownloadURL}')`;
      heroElement.style.backgroundSize = "cover";
      heroElement.style.backgroundPosition = "center";
      Logger.info(`✅ Hero-Bild gesetzt: ${metaData.landingPageImageDownloadURL}`);
    } else {
      Logger.warn("⚠️ Kein .landingpage-hero Element für Hero-Bild gefunden.");
    }
  }
}

function showErrorFallback(): void {
  document.body.innerHTML = `
    <main class="fallback-container">
      <h1>Fehler beim Laden der Seite</h1>
      <p>Die Standard-Landingpage konnte nicht geladen werden.</p>
      <p>Bitte versuche es später erneut oder kontaktiere den Anbieter.</p>
    </main>
  `;
}
